import React from "react";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import CustomButton from "../Button/CustomButton";
import {
  isChunkLoadError,
  shouldAutoReloadForChunkError,
} from "Utils/lazyWithRetry";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      chunkRecovering: false,
    };
  }

  static getDerivedStateFromError(error) {
    // ChunkLoadError: do not paint the Oops page while we attempt a one-shot reload.
    if (isChunkLoadError(error)) {
      return { hasError: false, chunkRecovering: true };
    }
    return { hasError: true, chunkRecovering: false };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    const chunkError = isChunkLoadError(error);
    const willReload =
      chunkError &&
      shouldAutoReloadForChunkError() &&
      typeof window !== "undefined";

    // Only ChunkLoadError (stale/missing JS chunk) may auto-reload.
    // All other errors keep the existing fallback UI — no behavior change.
    if (willReload) {
      window.location.reload();
      return;
    }

    this.setState({
      hasError: true,
      chunkRecovering: false,
      error,
      errorInfo,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      chunkRecovering: false,
    });
    window.location.href = "/dashboard";
  };

  render() {
    if (this.state.chunkRecovering && !this.state.hasError) {
      return null;
    }

    if (this.state.hasError) {
      // Show error details unless REACT_APP_ENV=production (set on Netlify prod)
      const isDevelopment = process.env.REACT_APP_ENV !== "production";

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          isDevelopment={isDevelopment}
          onReload={this.handleReload}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error, errorInfo, isDevelopment, onReload, onGoHome }) => {
  return (
    <div className="min-h-screen pt-16 pb-12 flex flex-col bg-white">
      <main className="flex-grow flex flex-col justify-center w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex-shrink-0 flex justify-center">
          <HiOutlineExclamationCircle className="text-red-500 h-24 w-24" />
        </div>
        <div className="py-10">
          <div className="text-center">
            <p className="text-sm font-semibold text-red-500 uppercase tracking-wide">
              Something went wrong
            </p>
            <h1 className="mt-2 text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl">
              Oops! An error occurred
            </h1>
            <p className="mt-4 text-base text-gray-600 max-w-2xl mx-auto">
              We're sorry, but something unexpected happened. Our team has been
              notified and is working to fix the issue. Please try refreshing
              the page or return to the dashboard.
            </p>

            {/* Development Error Details */}
            {isDevelopment && error && (
              <div className="mt-8 w-full">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-left">
                  <h2 className="text-lg font-semibold text-red-800 mb-4">
                    Error Details (Development Only)
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-red-700 mb-2">
                        Error Message:
                      </h3>
                      <pre className="bg-white border border-red-200 rounded p-3 text-sm text-red-900 overflow-auto max-h-40">
                        {error.toString()}
                      </pre>
                    </div>
                    {errorInfo && errorInfo.componentStack && (
                      <div>
                        <h3 className="text-sm font-semibold text-red-700 mb-2">
                          Component Stack:
                        </h3>
                        <pre className="bg-white border border-red-200 rounded p-3 text-sm text-red-900 overflow-auto max-h-60">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                    {error.stack && (
                      <div>
                        <h3 className="text-sm font-semibold text-red-700 mb-2">
                          Stack Trace:
                        </h3>
                        <pre className="bg-white border border-red-200 rounded p-3 text-sm text-red-900 overflow-auto max-h-60">
                          {error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CustomButton
                variant="primary"
                label="Reload Page"
                clickHandler={onReload}
              />
              <CustomButton
                variant="secondary"
                label="Go to Dashboard"
                clickHandler={onGoHome}
              />
            </div>
          </div>
        </div>
      </main>
      <footer className="flex-shrink-0 w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="flex justify-center">
          <p className="text-sm font-medium text-gray-500">
            &copy;{new Date().getFullYear()} All rights reserved by Vertex360!
          </p>
        </nav>
      </footer>
    </div>
  );
};

export default ErrorBoundary;
